<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-1 items-start gap-4" :class="gridColumns">
      <div v-if="showOpinion" class="flex flex-col gap-4 lg:col-span-2">
        <div v-if="analysis?.error" class="rounded-md border-l-4 border-red-400 bg-red-50 p-4 text-sm text-red-700">
          <p class="font-semibold">{{ t("unavailable") }}</p>
        </div>

        <div
          v-else-if="!analysis?.verdict"
          class="rounded-md border-l-4 border-gray-300 bg-gray-50 p-4 text-sm text-gray-700"
        >
          {{ t("pending") }}
        </div>

        <AiAnalysisVerdictCard v-else :analysis="analysis">
          <AiAnalysisFeedbackForm
            analysable-type="Claims::Declaration"
            :analysable-id="analysableId"
            analysis-type="general"
          />
        </AiAnalysisVerdictCard>

        <div v-if="canRetry && hasOutcome && !decidedDecision">
          <p v-if="retried" class="text-sm text-gray-700" data-test="retried">{{ t("retried") }}</p>
          <button
            v-else
            type="button"
            :disabled="retrying"
            class="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 focus:outline-hidden disabled:opacity-50"
            data-test="retry"
            @click="retry"
          >
            {{ t("retry_button") }}
          </button>
        </div>
      </div>

      <ClaimDecisionPanel
        v-if="claim"
        :class="{ 'lg:col-span-3': showOpinion }"
        :claim="claim"
        :agent-analysis="analysis?.verdict ? analysis : null"
        :decided-decision="decidedDecision"
        :initial-decision="analysis?.verdict || null"
        @decided="$emit('decided', $event)"
        @continue="$emit('continue')"
        @close="$emit('close')"
      >
        <template v-if="opinionCollapsible" #header-action>
          <button
            type="button"
            class="inline-flex shrink-0 items-center gap-0.5 text-xs font-semibold text-primary-dark hover:underline"
            :aria-expanded="opinionToggled"
            data-test="toggle-opinion"
            @click="opinionToggled = !opinionToggled"
          >
            <SparklesIcon class="mr-0.5 h-3.5 w-3.5" aria-hidden="true" />
            {{ opinionToggled ? t("hide_full_analysis") : t("show_full_analysis") }}
            <ChevronRightIcon
              class="h-3.5 w-3.5 transform transition-transform"
              :class="{ 'rotate-90': opinionToggled }"
              aria-hidden="true"
            />
          </button>
        </template>
      </ClaimDecisionPanel>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { ChevronRightIcon, SparklesIcon } from "@heroicons/vue/outline";
import { useRetryAnalysis } from "@/composables/useRetryAnalysis";
import AiAnalysisFeedbackForm from "@/components/claim/details/AiAnalysisFeedbackForm.vue";
import AiAnalysisVerdictCard from "@/components/claim/details/ai-analysis-flow/AiAnalysisVerdictCard.vue";
import ClaimDecisionPanel from "@/components/claim/details/action/ClaimDecisionPanel.vue";

export default {
  name: "AiAnalysisGeneralStep",

  components: { AiAnalysisFeedbackForm, AiAnalysisVerdictCard, ChevronRightIcon, ClaimDecisionPanel, SparklesIcon },

  setup(props) {
    const { t } = useI18n();
    const retried = ref(false);
    const { retrying, retryAnalysis } = useRetryAnalysis("general");
    // L'avis relancé n'est rendu que quelques minutes plus tard : on le signale sans recharger.
    const retry = () =>
      retryAnalysis(props.analysableId, (event) => {
        if (event === "retry-success") retried.value = true;
      });
    return { t, retrying, retried, retry };
  },

  props: {
    // Avis de l'agent général : vide tant que les analyses de documents ne sont pas terminées.
    analysis: {
      type: Object,
      default: null,
    },
    // Déclaration portant l'avis, à laquelle se rattache le feedback du gestionnaire.
    analysableId: {
      type: Number,
      required: true,
    },
    // La relance n'est possible que sur un sinistre en attente de traitement.
    canRetry: {
      type: Boolean,
      default: false,
    },
    // Sinistre sur lequel trancher : sans lui, l'étape n'affiche que l'avis.
    claim: {
      type: Object,
      default: null,
    },
    // Décision déjà confirmée dans ce parcours, portée par la modale.
    decidedDecision: {
      type: String,
      default: null,
    },
  },

  emits: ["decided", "continue", "close"],

  data() {
    return {
      opinionToggled: false,
    };
  },

  computed: {
    hasOutcome() {
      return Boolean(this.analysis?.error || this.analysis?.verdict);
    },
    // Face à une décision à prendre, l'ops va droit au formulaire : l'avis rendu ne s'affiche
    // qu'à sa demande, via le lien à droite du titre de la décision.
    opinionCollapsible() {
      return Boolean(this.claim && this.analysis?.verdict);
    },
    showOpinion() {
      return !this.opinionCollapsible || this.opinionToggled;
    },
    // L'avis déplié reprend sa colonne à gauche, les champs de la décision à droite.
    gridColumns() {
      if (!this.showOpinion) return null;
      return this.claim ? "lg:grid-cols-5" : "lg:grid-cols-3";
    },
  },
};
</script>

<i18n src="./AiAnalysisGeneralStep.i18n.json"></i18n>
